import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { setLoader, setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { getDataAPI, postDataAPI } from '../../utils/fetchData';
import { ReactComponent as GoogleLogo } from '../../assets/svg/Google__G__logo.svg';

const GoogleSheetsSync = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [workspaceId, setWorkspaceId] = useState('');
  const [secretToken, setSecretToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [bidirectionalWebhookUrl, setBidirectionalWebhookUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Google Sheets template copy link
  // Template ID: 116i8J4g1V1OXLE0FenKse1xZ86sRZnJllv9SL5dHCZs
  const TEMPLATE_COPY_URL = process.env.REACT_APP_GOOGLE_SHEETS_TEMPLATE_ID 
    ? `https://docs.google.com/spreadsheets/d/${process.env.REACT_APP_GOOGLE_SHEETS_TEMPLATE_ID}/copy`
    : 'https://docs.google.com/spreadsheets/d/116i8J4g1V1OXLE0FenKse1xZ86sRZnJllv9SL5dHCZs/copy';

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('integrations/google-sheets/settings', token);
      
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        setWorkspaceId(data.workspaceId || '');
        setSecretToken(data.secretToken || '');
        setWebhookUrl(data.webhookUrl || '');
        setBidirectionalWebhookUrl(data.bidirectionalWebhookUrl || '');
      } else {
        // If no settings exist, generate initial secret
        await handleRegenerateSecret();
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // If endpoint doesn't exist yet, set default webhook URL - always use production
      let backendUrl = process.env.REACT_APP_BACKEND_URL;
      // If no URL set, or if it's localhost/0.0.0.0, use production URL
      if (!backendUrl || backendUrl.includes('localhost') || backendUrl.includes('0.0.0.0') || backendUrl.includes('127.0.0.1')) {
        backendUrl = 'https://diz.ee';
      }
      setWebhookUrl(`${backendUrl}/api/v1/integrations/google-sheets/sync`);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateSecret = async () => {
    setRegenerating(true);
    dispatch(setLoader(true));
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI('integrations/google-sheets/regenerate-secret', {
        workspaceId: workspaceId || undefined
      }, token);
      
      if (response.data.status === 200 && response.data.data) {
        setSecretToken(response.data.data.secretToken);
        if (response.data.data.workspaceId) {
          setWorkspaceId(response.data.data.workspaceId);
        }
        dispatch(setSuccessAlert('Secret token regenerated successfully'));
        await loadSettings(); // Reload to get updated webhook URL
      } else {
        dispatch(setErrorAlert('Failed to regenerate secret token'));
      }
    } catch (error) {
      console.error('Error regenerating secret:', error);
      dispatch(setErrorAlert('Failed to regenerate secret token'));
    } finally {
      setRegenerating(false);
      dispatch(setLoader(false));
    }
  };

  const handleCopyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      dispatch(setErrorAlert('Failed to copy to clipboard'));
    }
  };

  const handleOpenTemplate = () => {
    if (!TEMPLATE_COPY_URL) {
      dispatch(setErrorAlert('Google Sheets template is not configured. Please contact support.'));
      return;
    }
    window.open(TEMPLATE_COPY_URL, '_blank');
  };

  const handleSaveWorkspaceId = async () => {
    if (!workspaceId.trim()) {
      dispatch(setErrorAlert('Workspace ID cannot be empty'));
      return;
    }

    setLoading(true);
    dispatch(setLoader(true));
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI('integrations/google-sheets/settings', {
        workspaceId: workspaceId.trim(),
        bidirectionalWebhookUrl: bidirectionalWebhookUrl.trim() || null
      }, token);
      
      if (response.data.status === 200) {
        dispatch(setSuccessAlert('Settings saved successfully'));
        await loadSettings();
      } else {
        dispatch(setErrorAlert('Failed to save settings'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      dispatch(setErrorAlert('Failed to save settings'));
    } finally {
      setLoading(false);
      dispatch(setLoader(false));
    }
  };

  const handleSaveBidirectionalWebhook = async () => {
    setLoading(true);
    dispatch(setLoader(true));
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI('integrations/google-sheets/settings', {
        workspaceId: workspaceId.trim() || undefined,
        bidirectionalWebhookUrl: bidirectionalWebhookUrl.trim() || null
      }, token);
      
      if (response.data.status === 200) {
        dispatch(setSuccessAlert('Bidirectional sync webhook URL saved successfully'));
        await loadSettings();
      } else {
        dispatch(setErrorAlert('Failed to save bidirectional sync webhook URL'));
      }
    } catch (error) {
      console.error('Error saving bidirectional webhook URL:', error);
      dispatch(setErrorAlert('Failed to save bidirectional sync webhook URL'));
    } finally {
      setLoading(false);
      dispatch(setLoader(false));
    }
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Google Sheets</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate('/connectors/google')}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Description */}
            <div className="mb-6">
              <p className="text-white text-[12px] opacity-70 leading-relaxed">
                Sync your bookings and catalog data using our ready-made Google Sheet template. 
                Any edits you make in the sheet will automatically update DIZEE.
              </p>
            </div>

            {/* Workspace ID Field */}
            <div className="mb-4 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
              <label className="text-white text-[11px] opacity-50 mb-2 block">Workspace ID</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={workspaceId}
                  onChange={(e) => setWorkspaceId(e.target.value)}
                  onBlur={handleSaveWorkspaceId}
                  placeholder="Enter your workspace ID"
                  className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.3)] focus:outline-none focus:border-[rgba(255,255,255,0.3)]"
                />
                <button
                  onClick={() => handleCopyToClipboard(workspaceId, 'workspaceId')}
                  disabled={!workspaceId}
                  className="px-3 py-2 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg text-white text-[11px] hover:bg-[rgba(255,255,255,0.15)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copiedField === 'workspaceId' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Secret Token Field */}
            <div className="mb-4 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="text-white text-[11px] opacity-50">Secret Token</label>
                <button
                  onClick={handleRegenerateSecret}
                  disabled={regenerating}
                  className="text-[#FF2300] text-[10px] hover:opacity-70 transition-all disabled:opacity-50"
                >
                  {regenerating ? 'Regenerating...' : 'Regenerate Secret'}
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={secretToken}
                  readOnly
                  className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] opacity-70 cursor-not-allowed"
                />
                <button
                  onClick={() => handleCopyToClipboard(secretToken, 'secretToken')}
                  disabled={!secretToken}
                  className="px-3 py-2 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg text-white text-[11px] hover:bg-[rgba(255,255,255,0.15)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copiedField === 'secretToken' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Webhook URL Field */}
            <div className="mb-4 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
              <label className="text-white text-[11px] opacity-50 mb-2 block">Webhook URL</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={webhookUrl}
                  readOnly
                  className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] opacity-70 cursor-not-allowed"
                />
                <button
                  onClick={() => handleCopyToClipboard(webhookUrl, 'webhookUrl')}
                  disabled={!webhookUrl}
                  className="px-3 py-2 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg text-white text-[11px] hover:bg-[rgba(255,255,255,0.15)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copiedField === 'webhookUrl' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Bidirectional Sync Webhook URL Field */}
            <div className="mb-6 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
              <label className="text-white text-[11px] opacity-50 mb-2 block">
                Bidirectional Sync Webhook URL (Optional)
              </label>
              <p className="text-white text-[10px] opacity-50 mb-2">
                To enable bidirectional sync (DIZEE → Google Sheets), deploy the script in your Google Sheet as a web app and paste the webhook URL here. 
                See setup instructions below for details.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={bidirectionalWebhookUrl}
                  onChange={(e) => setBidirectionalWebhookUrl(e.target.value)}
                  onBlur={handleSaveBidirectionalWebhook}
                  placeholder="https://script.google.com/macros/s/..."
                  className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.3)] focus:outline-none focus:border-[rgba(255,255,255,0.3)]"
                />
                <button
                  onClick={handleSaveBidirectionalWebhook}
                  disabled={loading}
                  className="px-3 py-2 bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg text-white text-[11px] hover:bg-[rgba(255,255,255,0.15)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {/* Open Template Button */}
            <button
              onClick={handleOpenTemplate}
              disabled={!TEMPLATE_COPY_URL}
              className={`w-full mb-6 rounded-lg p-4 transition-all flex items-center justify-center gap-2 ${
                TEMPLATE_COPY_URL 
                  ? 'bg-[#00CED1] hover:bg-[#00B8B8]' 
                  : 'bg-[rgba(255,255,255,0.1)] opacity-50 cursor-not-allowed'
              }`}
            >
              <GoogleLogo className="w-5 h-5" />
              <span className="text-white text-[12px] font-medium">
                {TEMPLATE_COPY_URL ? 'Open Google Sheets Template' : 'Template Not Available'}
              </span>
            </button>

            {/* Setup Guide */}
            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] rounded-lg mb-6">
              <p className="text-white text-[12px] font-medium mb-4">Setup Instructions</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">1.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    Click "Open Google Sheets Template" and choose "Make a copy".
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">2.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    Open the "Settings" tab in the copied sheet.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">3.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    Paste your Workspace ID into the Workspace ID field.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">4.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    Paste your Secret Token into the Secret Token field.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">5.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    Make sure the Webhook URL matches what is shown in DIZEE.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">6.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    (Optional) Paste your Google Sheet ID above to enable bidirectional sync (DIZEE → Google Sheets).
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-[#FF2300] text-[12px] font-semibold mt-0.5">7.</span>
                  <p className="text-white text-[11px] opacity-70 flex-1">
                    Use the "Bookings" and "Catalog" tabs. Every edit you make automatically syncs to DIZEE.
                  </p>
                </div>
              </div>
            </div>

            {/* Info Note */}
            <div className="p-4 bg-[rgba(255,215,0,0.05)] border border-[rgba(255,215,0,0.2)] rounded-lg">
              <p className="text-white text-[10px] opacity-70 leading-relaxed">
                <span className="font-semibold opacity-90">Note:</span> Use our Google Sheet template to manage your bookings and catalog. 
                When you make a change in the sheet, DIZEE updates automatically. Just copy the template, paste your 
                Workspace ID and Secret Token into the Settings tab, and begin using the Bookings and Catalog tabs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default GoogleSheetsSync;

